import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, AlertCircle, Phone } from 'lucide-react';
import { AuthLayout } from '@/components/AuthLayout';
import { PhoneInput, formatPhoneForSupabase, isValidUSPhone } from '@/components/PhoneInput';

export default function VerifyRequired() {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneOtp, setPhoneOtp] = useState('');
  const [showPhoneOtp, setShowPhoneOtp] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    // If either email OR phone is verified, redirect to home
    const emailVerified = !!user.email_confirmed_at;
    const phoneVerifiedFromProfile = userProfile?.phone_verified === true;

    if (emailVerified || phoneVerifiedFromProfile) {
      navigate('/');
    }
  }, [user, userProfile, navigate]);

  const handleResendEmail = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email,
      });

      if (error) throw error;
      toast.success('Verification email sent! Check your inbox.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend email');
    } finally {
      setLoading(false);
    }
  };

  const handleSendPhoneOtp = async () => {
    if (!isValidUSPhone(phone)) {
      toast.error('Please enter a valid US phone number');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = formatPhoneForSupabase(phone);

      // Use updateUser to attach phone to existing user (not signInWithOtp)
      const { error } = await supabase.auth.updateUser({
        phone: formattedPhone,
      });

      if (error) throw error;
      setShowPhoneOtp(true);
      toast.success('Verification code sent to your phone!');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneOtp || phoneOtp.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const formattedPhone = formatPhoneForSupabase(phone);

      // Use phone_change type since we used updateUser
      const { error } = await supabase.auth.verifyOtp({
        phone: formattedPhone,
        token: phoneOtp,
        type: 'phone_change',
      });

      if (error) throw error;

      // Update profile to mark phone as verified
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        await supabase
          .from('profiles')
          .update({ phone_verified: true, phone: formattedPhone })
          .eq('id', currentUser.id);
      }

      setPhoneVerified(true);
      toast.success('Phone verified successfully!');
      
      // Redirect to home (phone verification is sufficient)
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Email Verification Required
            </h1>
            <p className="text-muted-foreground">
              Please verify your email to access the BlueLux Team Portal
            </p>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">
                  Verification email sent to:
                </p>
                <p className="text-muted-foreground break-all">
                  {user?.email}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-2">
              <p>To complete your registration:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Check your email inbox</li>
                <li>Click the verification link</li>
                <li>Return here to sign in</li>
              </ol>
            </div>

            <Button
              onClick={handleResendEmail}
              className="w-full"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend Verification Email'}
            </Button>

            <div className="text-center">
              <Link
                to="/auth/login"
                className="text-sm text-primary hover:underline"
              >
                Back to Login
              </Link>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              Haven't received the email? Check your spam folder or click resend.
            </p>
          </div>
        </div>

        {/* Phone Verification Section */}
        <div className="bg-card border border-border rounded-lg shadow-lg p-8 mt-6">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Phone className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Phone Verification {phoneVerified && '✓'}
            </h2>
            <p className="text-muted-foreground">
              {phoneVerified 
                ? 'Your phone number has been verified!' 
                : 'Add and verify your phone number for account security'}
            </p>
          </div>

          {!showPhoneOtp ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneInput
                  id="phone"
                  value={phone}
                  onChange={setPhone}
                  disabled={phoneVerified}
                />
              </div>

              <Button
                onClick={handleSendPhoneOtp}
                className="w-full"
                disabled={loading || phoneVerified}
              >
                {loading ? 'Sending...' : phoneVerified ? '✓ Verified' : 'Send Verification Code'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="phoneOtp">Verification Code</Label>
                <Input
                  id="phoneOtp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={phoneOtp}
                  onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  disabled={phoneVerified}
                  className="text-center text-2xl tracking-widest"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Enter the 6-digit code sent to {phone}
                </p>
              </div>

              <Button
                onClick={handleVerifyPhone}
                className="w-full"
                disabled={loading || phoneVerified}
              >
                {loading ? 'Verifying...' : phoneVerified ? '✓ Verified' : 'Verify Phone'}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleSendPhoneOtp}
                disabled={loading || phoneVerified}
              >
                Resend Code
              </Button>
            </div>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}
